import React, { useCallback, useEffect, useMemo } from "react";
import { useMeetingStore } from "../../stores/useMeetingStore";
import useAxios, { UseAxiosProps } from "./useAxios";
import IMeeting from "../../api/IMeeting";
import { useActiveTopicStore } from "../../stores/useActiveTopicStore";
import { IRoundVote } from "../../api/IRound";
import useIsOpen from "../utils/useIsOpen";
import { toString } from "../../utilities/stringify";

type UseRoundControlProps = {
    axiosProps?: UseAxiosProps;
};

const useRoundControl = (params?: UseRoundControlProps) => {
    const meeting = useMeetingStore((state) => state.meeting);
    const { activeSubTopic, activeTask } = useActiveTopicStore(
        ({ activeSubTopic, activeTask }) => ({ activeSubTopic, activeTask })
    );

    const { error, loading, clearError, setError, post } = useAxios(
        params?.axiosProps
    );

    const [isCardShown, showCards, closeCards] = useIsOpen();

    // 揭牌（Show Votes）状态原本仅存于前端本地，host 刷新后会丢失，
    // 导致按钮从 "End Voting" 退回 "Show Votes"。这里按当前轮次 id 持久化到
    // localStorage：轮次一致则恢复揭牌，轮次结束/切换后自动失效清理。
    const revealStorageKey = meeting ? `cardsRevealed_${meeting.id}` : undefined;

    const persistReveal = () => {
        if (revealStorageKey && meeting?.votingRound) {
            localStorage.setItem(revealStorageKey, meeting.votingRound);
        }
        showCards();
    };

    const clearReveal = () => {
        if (revealStorageKey) localStorage.removeItem(revealStorageKey);
        closeCards();
    };

    // 刷新/重新进入后，依据当前进行中的轮次恢复揭牌状态
    useEffect(() => {
        if (!meeting) return;

        const votingRound = meeting.votingRound;
        if (!votingRound) {
            // 没有进行中的轮次：清理揭牌标记
            if (revealStorageKey) localStorage.removeItem(revealStorageKey);
            closeCards();
            return;
        }

        const stored = revealStorageKey
            ? localStorage.getItem(revealStorageKey)
            : null;
        if (stored === votingRound) showCards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meeting?.id, meeting?.votingRound]);

    const roundStarted =
        meeting?.votingRound !== undefined &&
        meeting.votingRound !== null &&
        meeting.votingRound.length > 0;

    // 判断是否正在显示投票结果
    const isShowingResults =
        !roundStarted &&
        meeting?.displayingRoundId !== undefined &&
        meeting.displayingRoundId !== null &&
        meeting.displayingRoundId.length > 0;

    // ?? useMemo ?? allParticipantsVoted ???
    const allParticipantsVoted = useMemo(() => {
        if (!meeting || meeting.participants.length === 0) {
            console.log("allParticipantsVoted: true (no participants)");
            return true;
        }

        // 只统计在线参会者，离线（已退出）的人不计入是否全部投票
        const onlineParticipants = meeting.participants.filter(
            participant => participant.isOnline !== false
        );

        if (onlineParticipants.length === 0) return true;

        const votedCount = onlineParticipants.filter(
            participant => participant.selectedPoker !== null && participant.selectedPoker !== undefined
        ).length;

        const result = votedCount === onlineParticipants.length;

        console.log(`allParticipantsVoted: ${result} (${votedCount}/${onlineParticipants.length} voted)`);
        
        // ?????????
        if (!result) {
            const notVoted = onlineParticipants
                .filter(p => !p.selectedPoker)
                .map(p => p.name);
            console.log("Participants not voted:", notVoted);
        }

        return result;
    }, [toString(meeting?.participants)]);

    const startRoundValid = () => {
        if (!meeting) return setError(`Missing meeting`);
        return true;
    };

    const endRoundValid = () => {
        if (!meeting) return setError(`Missing meeting`);
        return true;
    };

    const startRound = async () => {
        clearReveal();

        if (!startRoundValid()) return;

        console.log("Starting voting round for topic:", activeTask?.id ?? activeSubTopic?.id);

        let res = await post<IMeeting>(
            `/api/meeting/StartVotingRound?meetingId=${meeting!.id}`,
            {
                votingOn: activeTask?.id ?? activeSubTopic?.id,
            }
        );

        if (res) {
            console.log("Voting round started successfully");
        } else {
            console.error("Failed to start voting round");
        }
    };

    const endRound = async () => {
        if (!endRoundValid()) return;

        clearReveal();

        console.log("Ending voting round");

        // 只收集真正投了票的参会者，避免把没投票的人（value 为 undefined）一起发送，
        // 否则后端 RoundVoteDto.value 校验失败会返回 400。
        const votes = meeting!.participants
            .filter((participant) => participant.selectedPoker?.pokerValue)
            .map(
                (participant): IRoundVote => ({
                    userId: participant.userId,
                    value: participant.selectedPoker!.pokerValue,
                })
            );

        console.log("Collected votes:", votes);

        let res = await post<IMeeting>(
            `/api/meeting/EndVotingRound?meetingId=${meeting!.id}`,
            {
                meetingId: meeting!.id,
                votes: votes,
            }
        );

        if (res) {
            console.log("Voting round ended successfully");
        } else {
            console.error("Failed to end voting round");
        }
    };

    const onRoundClick = () => {
        if (!meeting) return;

        if (!roundStarted) {
            console.log("Starting new voting round");
            return startRound();
        }

        if (!isCardShown) {
            console.log("Showing cards");
            return persistReveal();
        }

        console.log("Ending voting round");
        endRound();
    };

    return {
        error,
        loading,
        roundStarted,
        isShowingResults,
        allParticipantsVoted,
        isCardShown,

        onRoundClick,
        clearError,
        setError,
    };
};

export default useRoundControl;
